import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Alert, AlertDescription } from './ui/alert';
import { CreditCard, Package, Clock, CheckCircle, XCircle, AlertCircle, Star, Building2, GraduationCap, Shield, Users, Wallet, ArrowRight, Info, RefreshCw, Euro } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MemberSubscriptionsNew = ({ getAuthHeader, onRefresh: onRefresh }) => {
  const [offers, setOffers] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [credits, setCredits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [showSubscribeDialog, setShowSubscribeDialog] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [activeTab, setActiveTab] = useState('offers');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [offersRes, subscriptionsRes, creditsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/subscriptions-management/offers`),
        axios.get(`${BACKEND_URL}/api/subscriptions-management/member/subscriptions`, { headers: getAuthHeader() }).catch(() => ({ data: { subscriptions: [] } })),
        axios.get(`${BACKEND_URL}/api/subscriptions-management/member/credits`, { headers: getAuthHeader() }).catch(() => ({ data: { credit_accounts: [] } }))
      ]);
      
      setOffers(offersRes.data.offers || []);
      setSubscriptions(subscriptionsRes.data.subscriptions || []);
      setCredits(creditsRes.data.credit_accounts || []);
    } catch {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    if (!selectedOffer) return;
    
    // Si paiement par carte, rediriger vers Stripe
    if (paymentMethod === 'card') {
      setSubscribing(true);
      try {
        // Mapper l'offre vers un package Stripe
        let packageId = 'membre_annuel';
        if (selectedOffer.category === 'formascop') {
          packageId = selectedOffer.price_annual > 150 ? 'formation_avancee' : 'formation_base';
        } else if (selectedOffer.credits) {
          if (selectedOffer.credits <= 10) packageId = 'credits_10';
          else if (selectedOffer.credits <= 25) packageId = 'credits_25';
          else packageId = 'credits_50';
        }
        
        const response = await axios.post(
          `${BACKEND_URL}/api/payments/checkout`,
          {
            package_id: packageId,
            origin_url: window.location.origin,
            metadata: {
              offer_id: selectedOffer.id,
              offer_name: selectedOffer.name
            }
          },
          { headers: getAuthHeader() }
        );
        
        if (response.data.checkout_url) {
          // Rediriger vers Stripe Checkout
          window.location.href = response.data.checkout_url;
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erreur lors de l\'initiation du paiement');
        setSubscribing(false);
      }
      return;
    }
    
    // Pour les autres méthodes de paiement (virement)
    setSubscribing(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/subscriptions-management/member/subscribe`,
        {
          offer_id: selectedOffer.id,
          plan_type: selectedOffer.price_annual ? 'annual' : (selectedOffer.price_monthly ? 'monthly' : 'credits'),
          payment_method: paymentMethod
        },
        { headers: getAuthHeader() }
      );
      
      toast.success('Souscription créée avec succès !');
      setShowSubscribeDialog(false);
      setSelectedOffer(null);
      fetchData();
      
      // Afficher les infos de paiement si virement
      if (paymentMethod === 'transfer' && response.data.payment_methods?.transfer) {
        toast.info(
          `Référence de virement: ${response.data.payment_methods.transfer.reference}`,
          { duration: 10000 }
        );
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la souscription');
    } finally {
      setSubscribing(false);
    }
  };

  const handleCancelSubscription = async (subscriptionId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir annuler cette souscription ?')) return;
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/subscriptions-management/member/subscriptions/${subscriptionId}/cancel`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success('Souscription annulée');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'annulation');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { label: 'Actif', variant: 'default', className: 'bg-green-500' },
      pending: { label: 'En attente', variant: 'secondary', className: 'bg-yellow-500' },
      expired: { label: 'Expiré', variant: 'destructive', className: 'bg-red-500' },
      cancelled: { label: 'Annulé', variant: 'outline', className: 'bg-gray-500' },
      suspended: { label: 'Suspendu', variant: 'destructive', className: 'bg-orange-500' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <Badge className={`${config.className} text-white`}>{config.label}</Badge>;
  };

  const getCategoryIcon = (category) => {
    const icons = {
      coppam: <Shield className="h-6 w-6 text-blue-600" />,
      formascop: <GraduationCap className="h-6 w-6 text-green-600" />,
      oscop_ge: <Users className="h-6 w-6 text-purple-600" />,
      batiscop: <Building2 className="h-6 w-6 text-orange-600" />
    };
    return icons[category] || <Package className="h-6 w-6 text-gray-600" />;
  };

  const getCategoryColor = (category) => {
    const colors = {
      coppam: 'border-blue-200 bg-blue-50',
      formascop: 'border-green-200 bg-green-50',
      oscop_ge: 'border-purple-200 bg-purple-50',
      batiscop: 'border-orange-200 bg-orange-50'
    };
    return colors[category] || 'border-gray-200 bg-gray-50';
  };

  const formatPrice = (offer) => {
    if (offer.price_annual) return `${offer.price_annual}€/an`;
    if (offer.price_monthly) return `${offer.price_monthly}€/mois`;
    if (offer.price) return `${offer.price}€`;
    if (offer.price_per_operation) return `${offer.price_per_operation}€/opération`;
    return 'Sur devis';
  };

  const getTotalCredits = () => {
    return credits.reduce((sum, c) => sum + (c.credits_balance || 0), 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header avec résumé */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-blue-600 to-blue-800 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Abonnements actifs</p>
                <p className="text-3xl font-bold">
                  {subscriptions.filter(s => s.status === 'active').length}
                </p>
              </div>
              <Package className="h-12 w-12 text-blue-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-600 to-green-800 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm">Crédits disponibles</p>
                <p className="text-3xl font-bold">{getTotalCredits()}</p>
              </div>
              <Wallet className="h-12 w-12 text-green-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-600 to-purple-800 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm">En attente</p>
                <p className="text-3xl font-bold">
                  {subscriptions.filter(s => s.status === 'pending').length}
                </p>
              </div>
              <Clock className="h-12 w-12 text-purple-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Onglets */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          <TabsTrigger value="offers" className="flex items-center gap-2">
            <Star className="h-4 w-4" />
            Offres disponibles
          </TabsTrigger>
          <TabsTrigger value="subscriptions" className="flex items-center gap-2">
            <Package className="h-4 w-4" />
            Mes abonnements
          </TabsTrigger>
          <TabsTrigger value="credits" className="flex items-center gap-2">
            <Wallet className="h-4 w-4" />
            Mes crédits
          </TabsTrigger>
        </TabsList>

        {/* Tab: Offres disponibles */}
        <TabsContent value="offers" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Toutes les offres</h3>
            <Button variant="outline" size="sm" onClick={fetchData}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Actualiser
            </Button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {offers.map((offer) => (
              <Card 
                key={offer.id} 
                className={`border-2 transition-all hover:shadow-lg ${getCategoryColor(offer.category)}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    {getCategoryIcon(offer.category)}
                    <Badge className="bg-slate-600 text-white text-xs capitalize font-semibold">
                      {offer.category?.replace('_', ' ')}
                    </Badge>
                  </div>
                  <CardTitle className="text-lg mt-2">{offer.name}</CardTitle>
                  <CardDescription className="text-sm">
                    {offer.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold text-gray-900">
                      {formatPrice(offer)}
                    </span>
                    {offer.credits && (
                      <Badge className="bg-green-700 text-white font-semibold">
                        {offer.credits} crédits
                      </Badge>
                    )}
                  </div>
                  
                  {offer.features && (
                    <ul className="space-y-1">
                      {offer.features.slice(0, 3).map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  )}
                  
                  <Button 
                    className="w-full"
                    onClick={() => {
                      setSelectedOffer(offer);
                      setShowSubscribeDialog(true);
                    }}
                  >
                    Souscrire
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Tab: Mes abonnements */}
        <TabsContent value="subscriptions" className="space-y-4">
          {subscriptions.length === 0 ? (
            <Card className="border-dashed border-2">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Package className="h-16 w-16 text-gray-300 mb-4" />
                <h3 className="text-lg font-semibold text-gray-600">Aucun abonnement</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Découvrez nos offres et souscrivez à votre premier abonnement
                </p>
                <Button onClick={() => setActiveTab('offers')}>
                  Voir les offres
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {subscriptions.map((sub) => (
                <Card key={sub.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {getCategoryIcon(sub.category)}
                        <div>
                          <h4 className="font-semibold">{sub.offer_name}</h4>
                          <p className="text-sm text-gray-500">{sub.plan_type}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        {getStatusBadge(sub.status)}
                        {sub.status === 'active' && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleCancelSubscription(sub.id)}
                          >
                            <XCircle className="h-4 w-4 mr-1" />
                            Annuler
                          </Button>
                        )}
                      </div>
                    </div>
                    
                    <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">Début</p>
                        <p className="font-medium">
                          {sub.start_date ? new Date(sub.start_date).toLocaleDateString('fr-FR') : '-'}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500">Fin</p>
                        <p className="font-medium">
                          {sub.end_date ? new Date(sub.end_date).toLocaleDateString('fr-FR') : 'Illimité'}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500">Paiement</p>
                        <Badge className="bg-slate-600 text-white capitalize font-semibold">
                          {sub.payment_method === 'card' ? 'Carte' : 'Virement'}
                        </Badge>
                      </div>
                      {sub.credits_remaining !== undefined && (
                        <div>
                          <p className="text-gray-500">Crédits restants</p>
                          <p className="font-medium text-green-600">{sub.credits_remaining}</p>
                        </div>
                      )}
                    </div>
                    
                    {sub.payment_status === 'pending' && (
                      <Alert className="mt-4 bg-yellow-50 border-yellow-200">
                        <AlertCircle className="h-4 w-4 text-yellow-600" />
                        <AlertDescription className="text-yellow-800">
                          En attente de paiement. Si vous avez choisi le virement, utilisez la référence: <strong>{sub.id?.slice(0, 8).toUpperCase()}</strong>
                        </AlertDescription>
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Tab: Mes crédits */}
        <TabsContent value="credits" className="space-y-4">
          {credits.length === 0 ? (
            <Card className="border-dashed border-2">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Wallet className="h-16 w-16 text-gray-300 mb-4" />
                <h3 className="text-lg font-semibold text-gray-600">Aucun crédit</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Achetez un pack de crédits pour accéder à nos services
                </p>
                <Button onClick={() => setActiveTab('offers')}>
                  Acheter des crédits
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {credits.map((credit) => (
                <Card key={credit.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        {getCategoryIcon(credit.offer_details?.category)}
                        <div>
                          <h4 className="font-semibold">{credit.offer_details?.name || 'Crédits'}</h4>
                          <p className="text-sm text-gray-500">{credit.offer_id}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div className="bg-green-50 rounded-lg p-3">
                        <p className="text-2xl font-bold text-green-600">{credit.credits_balance}</p>
                        <p className="text-xs text-gray-500">Disponibles</p>
                      </div>
                      <div className="bg-blue-50 rounded-lg p-3">
                        <p className="text-2xl font-bold text-blue-600">{credit.credits_total_purchased}</p>
                        <p className="text-xs text-gray-500">Achetés</p>
                      </div>
                      <div className="bg-purple-50 rounded-lg p-3">
                        <p className="text-2xl font-bold text-purple-600">{credit.credits_total_used}</p>
                        <p className="text-xs text-gray-500">Utilisés</p>
                      </div>
                    </div>
                    
                    <div className="mt-4">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-green-500 h-2 rounded-full transition-all"
                          style={{ 
                            width: `${credit.credits_total_purchased > 0 
                              ? (credit.credits_balance / credit.credits_total_purchased) * 100 
                              : 0}%` 
                          }}
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1 text-right">
                        {credit.credits_total_purchased > 0 
                          ? Math.round((credit.credits_balance / credit.credits_total_purchased) * 100) 
                          : 0}% restants
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Dialog de souscription */}
      <Dialog open={showSubscribeDialog} onOpenChange={setShowSubscribeDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedOffer && getCategoryIcon(selectedOffer.category)}
              Souscrire à {selectedOffer?.name}
            </DialogTitle>
            <DialogDescription>
              {selectedOffer?.description}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Résumé de l'offre */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-600">Prix</span>
                <span className="text-xl font-bold">{selectedOffer && formatPrice(selectedOffer)}</span>
              </div>
              {selectedOffer?.credits && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Crédits inclus</span>
                  <Badge className="bg-green-700 text-white font-semibold">{selectedOffer.credits}</Badge>
                </div>
              )}
            </div>
            
            {/* Choix du mode de paiement */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Mode de paiement</label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="card">
                    <div className="flex items-center gap-2">
                      <CreditCard className="h-4 w-4" />
                      Carte bancaire (Stripe)
                    </div>
                  </SelectItem>
                  <SelectItem value="transfer">
                    <div className="flex items-center gap-2">
                      <Euro className="h-4 w-4" />
                      Virement bancaire
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {paymentMethod === 'transfer' && (
              <Alert className="bg-blue-50 border-blue-200">
                <Info className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-800 text-sm">
                  Après validation, vous recevrez une référence de virement à mentionner lors du paiement. 
                  Votre abonnement sera activé dès réception du paiement.
                </AlertDescription>
              </Alert>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubscribeDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleSubscribe} disabled={subscribing}>
              {subscribing ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Traitement...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Confirmer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MemberSubscriptionsNew;
